/**
 * Cross-tab session synchronization utility
 * Uses BroadcastChannel API for modern browsers, falls back to localStorage events
 */

export enum SessionSyncEventType {
  LOGOUT = 'SESSION_LOGOUT',
  LOGIN = 'SESSION_LOGIN',
  SESSION_INVALIDATED = 'SESSION_INVALIDATED',
}

export interface SessionSyncEvent {
  type: SessionSyncEventType;
  timestamp: number;
}

type SessionSyncCallback = (event: SessionSyncEvent) => void;

class SessionSyncManager {
  private broadcastChannel: BroadcastChannel | null = null;

  private storageListener: ((e: StorageEvent) => void) | null = null;

  private callbacks: Set<SessionSyncCallback> = new Set();

  private isInitialized = false;

  private readonly CHANNEL_NAME = 'kilo-session-sync';

  private readonly STORAGE_KEY = 'kilo-session-sync-event';

  /**
   * Initialize the session sync manager
   * Sets up BroadcastChannel if available, otherwise falls back to localStorage events
   */
  public initialize(): void {
    if (this.isInitialized) {
      return;
    }

    // Try BroadcastChannel API first (modern browsers)
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        this.broadcastChannel = new BroadcastChannel(this.CHANNEL_NAME);
        this.broadcastChannel.onmessage = (event: MessageEvent<SessionSyncEvent>) => {
          // Ignore events sent by this tab
          this.handleEvent(event.data);
        };
      } catch (error) {
        console.warn('Failed to initialize BroadcastChannel, falling back to localStorage', error);
      }
    }

    // Fallback to localStorage events (for older browsers or if BroadcastChannel fails)
    if (!this.broadcastChannel && typeof window !== 'undefined' && window.localStorage) {
      this.storageListener = (e: StorageEvent) => {
        // Only handle our storage key and ignore events from the same tab
        if (e.key === this.STORAGE_KEY && e.newValue) {
          try {
            const event: SessionSyncEvent = JSON.parse(e.newValue);
            this.handleEvent(event);
          } catch (error) {
            console.error('Failed to parse session sync event from localStorage', error);
          }
        }
      };
      window.addEventListener('storage', this.storageListener);
    }

    this.isInitialized = true;
  }

  /**
   * Cleanup listeners when no longer needed
   */
  public cleanup(): void {
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
      this.broadcastChannel = null;
    }

    if (this.storageListener) {
      window.removeEventListener('storage', this.storageListener);
      this.storageListener = null;
    }

    this.callbacks.clear();
    this.isInitialized = false;
  }

  /**
   * Subscribe to session sync events
   */
  public subscribe(callback: SessionSyncCallback): () => void {
    if (!this.isInitialized) {
      this.initialize();
    }

    this.callbacks.add(callback);

    // Return unsubscribe function
    return () => {
      this.callbacks.delete(callback);
    };
  }

  /**
   * Broadcast a session sync event to all other tabs
   */
  public broadcast(eventType: SessionSyncEventType): void {
    if (!this.isInitialized) {
      this.initialize();
    }

    const event: SessionSyncEvent = {
      type: eventType,
      timestamp: Date.now(),
    };

    // Send via BroadcastChannel if available
    if (this.broadcastChannel) {
      try {
        this.broadcastChannel.postMessage(event);
        return;
      } catch (error) {
        console.warn('Failed to broadcast via BroadcastChannel, falling back to localStorage', error);
      }
    }

    // Fallback to localStorage
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        // Set and immediately remove to trigger storage event
        // We use a timestamp to ensure the event fires even if the value is the same
        window.localStorage.setItem(this.STORAGE_KEY, JSON.stringify(event));
        // Small delay before removing to ensure the event is processed
        setTimeout(() => {
          window.localStorage.removeItem(this.STORAGE_KEY);
        }, 100);
      } catch (error) {
        console.error('Failed to broadcast session sync event via localStorage', error);
      }
    }
  }

  /**
   * Handle incoming session sync events
   */
  private handleEvent(event: SessionSyncEvent): void {
    // Notify all subscribers
    this.callbacks.forEach(callback => {
      try {
        callback(event);
      } catch (error) {
        console.error('Error in session sync callback', error);
      }
    });
  }
}

// Singleton instance
export const sessionSyncManager = new SessionSyncManager();
